export default class LiveSearch extends Core.Component {
  constructor(el, context) {
    super(el, context);
    this.cachedResults = {};
    this.input = el.querySelector('[data-live-search-query-input]');
    this.predictiveSearchResultsContainer = el.querySelector('[data-live-search-results-container]');
    this.predictiveSearchResults = el.querySelector('[data-live-search-results]');
    const form = el.querySelector('[data-live-search-form]');
    this.resourceTypes = `product${this.data.settings.show_posts_and_pages ? ',article,page' : ''}`;
    this.loadingText = this.data.loading_text;

    this.animation = Core.Animations.transition({ el: this.predictiveSearchResultsContainer, state: 'closed' });
    this.isOpen = false;

    this.events.register(form, 'submit', e => this._onFormSubmit(e));
    this.events.register(this.input, 'input', e => this._onChange(e));
    this.events.register(this.input, 'focus', e => this._onFocus(e));
    this.events.register(el, 'keyup', e => this._onKeyup(e));
    this.events.register(el, 'keydown', e => this._onKeydown(e));
  }

  onMount() {
    this.siteOverlay = Core.Components.getComponent('site-overlay');
  }

  focus() {
    this.input.focus();
  }

  close() {
    return this._close();
  }

  _getQuery() {
    return this.input.value.trim();
  }

  _onChange() {
    const searchTerm = this._getQuery();

    if (!searchTerm.length) {
      this._close(true);
      return;
    }

    this._getSearchResults(searchTerm);
  }

  _onFormSubmit(event) {
    // Submit buttons should always submit form
    if (event.submitter && event.submitter.type === 'submit') return;

    if (!this._getQuery().length || this.el.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  _onFocus() {
    const searchTerm = this._getQuery();

    if (!searchTerm.length) return;

    if (this.el.getAttribute('results') === 'true') {
      this._open();
    } else {
      this._getSearchResults(searchTerm);
    }
  }

  _onKeyup(event) {
    if (!this._getQuery().length) this._close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this._switchOption('up');
        break;
      case 'ArrowDown':
        this._switchOption('down');
        break;
      case 'Enter':
        this._selectOption();
        break;
      default:
        break;
    }
  }

  _onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp'
      || event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  _switchOption(direction) {
    if (!this.isOpen) return;

    const moveUp = direction === 'up';
    const selectedElement = this.el.querySelector('[aria-selected="true"]');
    const allElements = this.el.querySelectorAll('[data-predictive-search-result]');
    let activeElement = this.el.querySelector('[data-predictive-search-result]');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';
    const selectedIndex = Array.prototype.indexOf.call(allElements, selectedElement);

    if (!moveUp && selectedElement) {
      activeElement = allElements[selectedIndex + 1] || allElements[0];
    } else if (moveUp) {
      activeElement = allElements[selectedIndex - 1] || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    activeElement.focus();

    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this._setLiveRegionText(activeElement.innerText);
  }

  _selectOption() {
    const actionSelectors = [
      '[data-predictive-search-result-action]',
      'a',
      'button',
    ];

    const selectedAction = actionSelectors.reduce((selected, actionSelector) => {
      if (selected) return selected;

      return this.el.querySelector(`[aria-selected="true"] ${actionSelector}`);
    }, null);

    if (selectedAction) selectedAction.click();
  }

  _getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this._setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this._renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${window.PXUTheme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=${this.resourceTypes}&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then(response => {
        if (!response.ok) {
          const error = new Error(response.status);
          this._close();
          throw error;
        }

        return response.text();
      })
      .then(text => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this._renderSearchResults(resultsMarkup);
      })
      .catch(error => {
        this._close();
        throw error;
      });
  }

  _setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.el.querySelector('[data-live-search-status]');
    this._setLiveRegionText(this.loadingText);
    this.el.setAttribute('loading', true);
  }

  _setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  _renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.el.setAttribute('results', true);

    this._setLiveRegionResults();
    this._open();
  }

  _setLiveRegionResults() {
    this.el.removeAttribute('loading');
    this._setLiveRegionText(this.el.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  _open() {
    if (this.isOpen) return;
    this.isOpen = true;

    Core.Utils.scrollLock(this.predictiveSearchResults, this.predictiveSearchResults, false);

    this.animation.animateTo('open')
      .then(() => {
        if (this.siteOverlay) this.siteOverlay.show({ belowHeader: true });
        this.input.setAttribute('aria-expanded', true);
      });
  }

  _close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';

      this.el.removeAttribute('results');
    }
    if (this.siteOverlay) this.siteOverlay.hide();

    Core.Utils.scrollUnlock(this.predictiveSearchResults);

    return this.animation.animateTo('closed')
      .then(() => {
        this.isOpen = false;
        const selected = this.el.querySelector('[aria-selected="true"]');
        if (selected) selected.setAttribute('aria-selected', false);

        this.input.setAttribute('aria-activedescendant', '');
        this.input.setAttribute('aria-expanded', false);
      });
  }
}
